import { createContext, useContext, useState, useEffect, useCallback } from "react";

const TenantContext = createContext(null);

function getSubdomain() {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  if (parts.length >= 3) {
    const sub = parts[0];
    if (["www", "app", "localhost"].includes(sub)) return null;
    if (hostname.includes("preview.emergentagent.com")) return null;
    return sub;
  }
  return null;
}

function getTestTenant() {
  const params = new URLSearchParams(window.location.search);
  return params.get("tenant");
}

function getStoredTenant() {
  return sessionStorage.getItem("tenant_slug");
}

// Extract tenant slug from URL path (e.g., /byk-demo/login → "byk-demo")
function getPathTenant() {
  const path = window.location.pathname;
  const parts = path.split("/").filter(Boolean);
  if (parts.length === 0) return null;
  // Known app routes that are NOT tenant slugs
  const appRoutes = ["login", "home", "admin", "reports", "analytics", "consolidated", "notifications", "report", "api"];
  // If the first path segment is NOT a known route, it's a tenant slug
  if (!appRoutes.includes(parts[0])) {
    return parts[0];
  }
  return null;
}

// Check if the current URL is a base domain route (no tenant slug in path)
function isBaseDomainRoute() {
  const pathTenant = getPathTenant();
  const subdomain = getSubdomain();
  // If there's no slug in the URL path AND no subdomain-based tenant → base domain
  return !pathTenant && !subdomain;
}

function resolveSubdomain() {
  // Priority: subdomain (host-based) → query param → path-based slug → sessionStorage
  // But if we're on a base domain route, ignore sessionStorage to prevent stale branding
  const sub = getSubdomain();
  if (sub) return sub;
  const test = getTestTenant();
  if (test) return test;
  const pathSlug = getPathTenant();
  if (pathSlug) return pathSlug;
  // Only use sessionStorage if the current URL also has a slug context
  // If on base domain (/login, /admin, /reports), do NOT use stale sessionStorage
  if (!isBaseDomainRoute()) {
    return getStoredTenant();
  }
  return null;
}

export function TenantProvider({ children }) {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subdomain, setSubdomain] = useState(() => resolveSubdomain());

  // Clear sessionStorage when navigating to base domain routes
  useEffect(() => {
    if (isBaseDomainRoute()) {
      sessionStorage.removeItem("tenant_slug");
      if (subdomain) {
        setSubdomain(null);
        setTenant(null);
      }
    }
  }, []);

  // Re-check subdomain when URL changes (e.g., TenantRouteWrapper sets sessionStorage)
  const refreshSubdomain = useCallback(() => {
    const resolved = resolveSubdomain();
    if (resolved !== subdomain) {
      if (resolved) {
        setSubdomain(resolved);
      }
    }
  }, [subdomain]);

  // Listen for changes periodically (short-lived — stops once subdomain is established)
  useEffect(() => {
    if (subdomain) return; // Already resolved, no need to poll
    const interval = setInterval(refreshSubdomain, 300);
    const timer = setTimeout(refreshSubdomain, 50);
    return () => { clearInterval(interval); clearTimeout(timer); };
  }, [subdomain, refreshSubdomain]);

  // Fetch tenant config when subdomain is set
  useEffect(() => {
    if (!subdomain) {
      setLoading(false);
      return;
    }
    // Store to sessionStorage for in-session consistency
    sessionStorage.setItem("tenant_slug", subdomain);

    const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";
    fetch(`${API_BASE}/tenant/config?subdomain=${encodeURIComponent(subdomain)}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.tenant) {
          setTenant(data.tenant);
          applyTenantTheme(data.tenant);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [subdomain]);

  return (
    <TenantContext.Provider value={{ tenant, subdomain, loading, refreshSubdomain }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTenant must be inside TenantProvider");
  return ctx;
}

function applyTenantTheme(tenant) {
  if (!tenant) return;
  const root = document.documentElement;
  if (tenant.primary_color) {
    const hsl = hexToHSL(tenant.primary_color);
    if (hsl) root.style.setProperty("--primary", `${hsl.h} ${hsl.s}% ${hsl.l}%`);
  }
  if (tenant.accent_color) {
    const hsl = hexToHSL(tenant.accent_color);
    if (hsl) root.style.setProperty("--accent", `${hsl.h} ${hsl.s}% ${hsl.l}%`);
  }
}

function hexToHSL(hex) {
  hex = hex.replace("#", "");
  if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}
